import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

import { apiClient, handleApiError } from '@/config/api';
import { EmployeeProfileResponse } from '@/types/api';

/**
 * Minimal PII-free projection returned by `/employee/directory` — the source
 * for staff pickers (new-conversation, time-off sub picker). Deliberately omits
 * email/phone/DOB the picker has no business handling.
 */
export interface DirectoryEntry {
  id: number;
  fullName: string;
  departmentName?: string;
  admin?: boolean;
}

interface EmployeeState {
  currentEmployee: EmployeeProfileResponse | null;
  employees: EmployeeProfileResponse[];
  instructors: EmployeeProfileResponse[];
  directory: DirectoryEntry[];
  isLoading: boolean;
  isLoadingDirectory: boolean;
  error: string | null;
  searchResults: EmployeeProfileResponse[];
  isSearching: boolean;
}

const initialState: EmployeeState = {
  currentEmployee: null,
  employees: [],
  instructors: [],
  directory: [],
  isLoading: false,
  isLoadingDirectory: false,
  error: null,
  searchResults: [],
  isSearching: false,
};

// Async Thunks
export const getEmployeeProfile = createAsyncThunk(
  'employee/getProfile',
  async (employeeId: number, { rejectWithValue }) => {
    try {
      const response = await apiClient.get<EmployeeProfileResponse>(`/employee/${employeeId}`);
      return response;
    } catch (error) {
      return rejectWithValue(handleApiError(error));
    }
  }
);

export interface UpdateContactPayload {
  phone?: string;
  address?: string;
  city?: string;
  zip?: string;
}

/** Staff self-service: update the current user's own contact fields. */
export const updateEmployeeContact = createAsyncThunk(
  'employee/updateContact',
  async (payload: UpdateContactPayload, { rejectWithValue }) => {
    try {
      return await apiClient.patch<EmployeeProfileResponse>('/employee/profile', payload);
    } catch (error) {
      return rejectWithValue(handleApiError(error));
    }
  }
);

export const getActiveEmployees = createAsyncThunk(
  'employee/getActiveEmployees',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiClient.get<EmployeeProfileResponse[]>('/employee');
      return response;
    } catch (error) {
      return rejectWithValue(handleApiError(error));
    }
  }
);

export const getInstructors = createAsyncThunk(
  'employee/getInstructors',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiClient.get<EmployeeProfileResponse[]>('/employee/instructors');
      return response;
    } catch (error) {
      return rejectWithValue(handleApiError(error));
    }
  }
);

export const searchEmployees = createAsyncThunk(
  'employee/search',
  async (searchTerm: string, { rejectWithValue }) => {
    try {
      if (searchTerm.trim().length < 2) {
        return [];
      }
      const response = await apiClient.get<EmployeeProfileResponse[]>(
        `/employee/search?term=${encodeURIComponent(searchTerm)}`
      );
      return response;
    } catch (error) {
      return rejectWithValue(handleApiError(error));
    }
  }
);

/** Full active staff directory — the sub-picker source (cached & testable). */
export const getEmployeeDirectory = createAsyncThunk(
  'employee/getDirectory',
  async (_, { rejectWithValue }) => {
    try {
      return await apiClient.get<DirectoryEntry[]>('/employee/directory');
    } catch (error) {
      return rejectWithValue(handleApiError(error));
    }
  }
);

export const getEmployeesByDepartment = createAsyncThunk(
  'employee/getByDepartment',
  async (departmentId: number, { rejectWithValue }) => {
    try {
      const response = await apiClient.get<EmployeeProfileResponse[]>(
        `/employee/department/${departmentId}`
      );
      return response;
    } catch (error) {
      return rejectWithValue(handleApiError(error));
    }
  }
);

// Slice
const employeeSlice = createSlice({
  name: 'employee',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearSearchResults: (state) => {
      state.searchResults = [];
      state.isSearching = false;
    },
    setCurrentEmployee: (state, action) => {
      state.currentEmployee = action.payload;
    },
  },
  extraReducers: (builder) => {
    // Get Employee Profile
    builder
      .addCase(getEmployeeProfile.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateEmployeeContact.fulfilled, (state, action) => {
        state.currentEmployee = action.payload;
      })
      .addCase(getEmployeeProfile.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentEmployee = action.payload;
      })
      .addCase(getEmployeeProfile.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // Get Active Employees
    builder
      .addCase(getActiveEmployees.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getActiveEmployees.fulfilled, (state, action) => {
        state.isLoading = false;
        state.employees = action.payload;
      })
      .addCase(getActiveEmployees.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // Get Instructors
    builder
      .addCase(getInstructors.fulfilled, (state, action) => {
        state.instructors = action.payload;
      })
      .addCase(getInstructors.rejected, (state, action) => {
        state.error = action.payload as string;
      });

    // Search Employees
    builder
      .addCase(searchEmployees.pending, (state) => {
        state.isSearching = true;
        state.error = null;
      })
      .addCase(searchEmployees.fulfilled, (state, action) => {
        state.isSearching = false;
        state.searchResults = action.payload;
      })
      .addCase(searchEmployees.rejected, (state, action) => {
        state.isSearching = false;
        state.error = action.payload as string;
      });

    // Get Employee Directory
    builder
      .addCase(getEmployeeDirectory.pending, (state) => {
        state.isLoadingDirectory = true;
        state.error = null;
      })
      .addCase(getEmployeeDirectory.fulfilled, (state, action) => {
        state.isLoadingDirectory = false;
        state.directory = action.payload;
      })
      .addCase(getEmployeeDirectory.rejected, (state, action) => {
        state.isLoadingDirectory = false;
        state.error = action.payload as string;
      });

    // Get Employees by Department
    builder
      .addCase(getEmployeesByDepartment.fulfilled, (state, action) => {
        state.employees = action.payload;
      })
      .addCase(getEmployeesByDepartment.rejected, (state, action) => {
        state.error = action.payload as string;
      });
  },
});

export const { clearError, clearSearchResults, setCurrentEmployee } = employeeSlice.actions;
export default employeeSlice.reducer;